import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Shield,
    LogOut,
    ChevronRight,
    Building,
    User,
    Package,
    CalendarDays,
    LayoutDashboard,
    MapPin,
    Trophy,
    Sparkles,
    Zap
} from 'lucide-react';

import { authService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ onClose }) => {
    const location = useLocation();
    const { logout } = useAuth();
    const [hoveredItem, setHoveredItem] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // ============================================================
    // ✅ EFECTO PRINCIPAL – CARGA DE DATOS DEL USUARIO (DESCIFRADO)
    // ============================================================
    useEffect(() => {
        const loadUserData = () => {
            try {
                // ✅ Usar authService.getCurrentUser() que ya descifra
                const user = authService.getCurrentUser();
                setUserData(user);

                // Determinar si es admin (basado en rol, si existe)
                const userRole = user?.rol?.toLowerCase();
                const isAdminUser = ['admin', 'administrador', 'administracion', 'administrator'].includes(userRole);
                setIsAdmin(isAdminUser);
            } catch (error) {
                console.error('Error al cargar datos del usuario:', error);
            }
        };

        loadUserData();

        // Escuchar cambios en localStorage (para otras pestañas)
        const handleStorageChange = (e) => {
            // La clave correcta es 'user_encrypted' (definida en authService)
            if (e.key === 'user_encrypted') {
                loadUserData();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // Intervalo para actualizar dentro de la misma pestaña
        const intervalId = setInterval(loadUserData, 3000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(intervalId);
        };
    }, []);

    const stars = useMemo(() => {
        return Array.from({ length: 30 }, (_, i) => ({
            id: i,
            size: Math.random() * 2 + 1.5,
            left: Math.random() * 100,
            delay: Math.random() * 10,
            duration: Math.random() * 8 + 6,
            opacity: Math.random() * 0.5 + 0.2,
        }));
    }, []);

    const mainNavigation = useMemo(() => [
        {
            name: 'Inicio',
            href: '/admin/dashboard',
            icon: LayoutDashboard,
            description: 'Inicio-principal',
            color: 'indigo'
        },
        {
            name: 'Productos',
            href: '/admin/productos',
            icon: Package,
            description: 'Catálogo e inventario',
            color: 'indigo'
        },
        {
            name: 'Verificar DNI',
            href: '/admin/verificarcodigo',
            icon: Package,
            description: 'Verificar codigo afiliacion',
            color: 'indigo'
        },
        {
            name: 'Eventos',
            href: '/admin/eventos',
            icon: CalendarDays,
            description: 'Calendario y actividades',
            color: 'violet'
        },
        {
            name: 'Centro servicios',
            href: '/admin/centroservicios',
            icon: MapPin,
            description: 'Centro de Servicios',
            color: 'violet'
        },
        {
            name: 'Rangos',
            href: '/admin/rangos',
            icon: Trophy,
            description: 'Rangos / Jerarquía',
            color: 'violet'
        }
    ], []);

    const adminNavigation = useMemo(() => [
        {
            name: 'Usuarios',
            href: '/admin/users',
            icon: User,
            description: 'Control de accesos',
            adminOnly: true,
            color: 'rose'
        }
    ], []);

    const navigation = useMemo(() => {
        return isAdmin ? [...mainNavigation, ...adminNavigation] : mainNavigation;
    }, [isAdmin, mainNavigation, adminNavigation]);

    const handleNavigation = () => {
        if (onClose && window.innerWidth < 1024) onClose();
    };

    const handleLogout = () => {
        if (onClose && window.innerWidth < 1024) onClose();
        logout();
    };

    const getInitials = (nombre, apellido) => {
        if (!nombre && !apellido) return 'U';
        if (!apellido) return nombre?.charAt(0)?.toUpperCase() || 'U';
        return `${nombre?.charAt(0) || ''}${apellido?.charAt(0) || ''}`.toUpperCase();
    };

    const containerVariants = {
        hidden: { opacity: 0, x: -15 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.08 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 8 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] }
        }
    };

    return (
        <motion.aside
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="relative flex flex-col w-64 bg-gradient-to-b from-indigo-50/80 via-white to-slate-50/70 backdrop-blur-xl text-slate-800 overflow-hidden border-r border-slate-200 shadow-[4px_0_24px_rgba(15,23,42,0.04)]"
        >
            {/* --- FONDO DE ESTRELLAS ANIMADAS --- */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
                {stars.map((star) => (
                    <motion.div
                        key={star.id}
                        className="absolute rounded-full bg-indigo-300"
                        style={{
                            width: `${star.size}px`,
                            height: `${star.size}px`,
                            left: `${star.left}%`,
                            top: '-10px',
                        }}
                        initial={{ y: -10, opacity: 0, scale: 1.8 }}
                        animate={{
                            y: ['0%', '100vh'],
                            opacity: [0, star.opacity, star.opacity * 0.7, 0],
                            scale: [1.8, 1.2, 0.6, 0.1],
                            x: [0, Math.random() * 8 - 4],
                        }}
                        transition={{
                            duration: star.duration,
                            delay: star.delay,
                            repeat: Infinity,
                            ease: 'linear',
                        }}
                    />
                ))}
            </div>
            
            {/* Capa de degradado superior pronunciada */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-indigo-100/60 via-indigo-50/30 to-transparent pointer-events-none" />
            
            {/* Línea de acento superior */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-300 to-transparent" />

            <div className="relative z-10 flex flex-col h-full">
                
                {/* --- SECCIÓN DE PERFIL --- */}
                <motion.div variants={itemVariants} className="p-5 pb-6">
                    <div className="flex flex-col items-center text-center relative">
                        {/* Avatar Premium con Glow */}
                        <div className="relative mb-3 group">
                            <div className="absolute -inset-1 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-full opacity-70 group-hover:opacity-100 blur-md transition duration-500"></div>
                            <div className="relative w-16 h-16 rounded-full p-[2px] bg-white shadow-lg">
                                <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
                                    {userData?.logo_empresa ? (
                                        <img
                                            src={userData.logo_empresa}
                                            alt="Logo"
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = `https://ui-avatars.com/api/?name=${getInitials(userData.nombre)}&background=6366f1&color=fff&size=128`;
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-bold text-lg tracking-tight">
                                            {getInitials(userData?.nombre, userData?.apellido)}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            {/* Indicador de estado con pulso */}
                            <div className={`absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full border-2 border-white shadow-sm flex items-center justify-center ${isAdmin ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                                {isAdmin && <Shield className="w-2 h-2 text-white" />}
                                <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${isAdmin ? 'bg-rose-400' : 'bg-emerald-400'}`}></span>
                            </div>
                        </div>

                        <h2 className="font-bold text-slate-900 text-base tracking-tight">
                            {userData?.nombre || 'Usuario'}
                        </h2>
                        
                        {userData?.rol && (
                            <span className={`mt-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold tracking-wide border ${
                                isAdmin 
                                ? 'bg-rose-50 text-rose-700 border-rose-200 shadow-sm' 
                                : 'bg-indigo-50 text-indigo-700 border-indigo-200 shadow-sm'
                            }`}>
                                {isAdmin ? 'Administrador' : userData.rol}
                            </span>
                        )}

                        {userData?.nombre_empresa && (
                            <div className="flex items-center justify-center mt-3 text-slate-600 bg-white/80 backdrop-blur-sm px-2.5 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                                <Building className="w-3 h-3 mr-1.5 text-indigo-500 flex-shrink-0" />
                                <p className="text-[11px] font-semibold truncate max-w-[130px]">
                                    {userData.nombre_empresa}
                                </p>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Separador con degradado */}
                <div className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent mb-2" />

                {/* --- NAVEGACIÓN --- */}
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 overflow-y-auto custom-scrollbar py-2">
                        <motion.div variants={itemVariants} className="px-4 mb-3 mt-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] flex items-center gap-1.5">
                                <Sparkles className="w-3 h-3 text-indigo-500" />
                                Menú
                            </p>
                        </motion.div>

                        <nav className="px-2.5 space-y-1">
                            {navigation.map((item) => {
                                const Icon = item.icon;
                                const isActive = location.pathname === item.href;
                                const isHovered = hoveredItem === item.name;
                                const themeColor = item.color;

                                return (
                                    <motion.div
                                        key={item.name}
                                        variants={itemVariants}
                                        onHoverStart={() => setHoveredItem(item.name)}
                                        onHoverEnd={() => setHoveredItem(null)}
                                    >
                                        <Link
                                            to={item.href}
                                            onClick={handleNavigation}
                                            className="relative block group"
                                        >
                                            {isActive && (
                                                <motion.div
                                                    layoutId="active-nav-background"
                                                    className={`absolute inset-0 rounded-lg bg-gradient-to-br from-${themeColor}-50 to-${themeColor}-100/50 border border-${themeColor}-200 shadow-sm`}
                                                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                                                />
                                            )}

                                            <div className={`relative flex items-center px-2.5 py-2.5 rounded-lg transition-all duration-200 ${
                                                isActive ? `text-${themeColor}-700` : 'text-slate-600 hover:bg-white/80 hover:shadow-sm hover:text-slate-900'
                                            }`}>
                                                
                                                <div className={`w-8 h-8 rounded-md flex items-center justify-center mr-2.5 transition-all duration-300 ${
                                                    isActive 
                                                        ? `bg-gradient-to-br from-${themeColor}-500 to-${themeColor}-600 text-white shadow-md shadow-${themeColor}-200` 
                                                        : `bg-slate-100 text-slate-500 group-hover:bg-white group-hover:shadow-sm group-hover:text-${themeColor}-500`
                                                }`}>
                                                    <Icon className="w-4 h-4" strokeWidth={2} />
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <span className={`font-semibold text-[13px] block truncate tracking-tight transition-colors ${
                                                        isActive ? `text-${themeColor}-900` : 'text-slate-700 group-hover:text-slate-900'
                                                    }`}>
                                                        {item.name}
                                                    </span>
                                                    <span className={`text-[11px] font-medium truncate block mt-0.5 transition-colors ${
                                                        isActive ? `text-${themeColor}-600` : 'text-slate-400 group-hover:text-slate-500'
                                                    }`}>
                                                        {item.description}
                                                    </span>
                                                </div>

                                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                                    {item.adminOnly && (
                                                        <Zap className="w-3.5 h-3.5 text-rose-500 fill-rose-500/20" />
                                                    )}
                                                    {isHovered && !isActive && (
                                                        <motion.div
                                                            initial={{ opacity: 0, x: -5 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            exit={{ opacity: 0, x: -5 }}
                                                            transition={{ duration: 0.2 }}
                                                        >
                                                            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                                                        </motion.div>
                                                    )}
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                {/* --- FOOTER / LOGOUT --- */}
                <motion.div variants={itemVariants} className="p-3.5 mt-auto">
                    <div className="relative p-0.5 rounded-xl bg-gradient-to-b from-slate-200 to-slate-100">
                        <motion.button
                            onClick={handleLogout}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full flex items-center justify-between p-2.5 bg-white rounded-[10px] border border-slate-200 shadow-sm hover:border-rose-300 hover:bg-gradient-to-br hover:from-rose-50 hover:to-white hover:shadow-md transition-all duration-300 group"
                        >
                            <div className="flex items-center space-x-2.5">
                                <div className="w-8 h-8 bg-gradient-to-br from-slate-50 to-slate-100 rounded-md flex items-center justify-center group-hover:from-rose-100 group-hover:to-rose-50 transition-colors duration-300 border border-slate-200">
                                    <LogOut className="w-4 h-4 text-slate-600 group-hover:text-rose-600 transition-colors duration-300" strokeWidth={2} />
                                </div>
                                <div className="text-left">
                                    <p className="text-[13px] font-semibold text-slate-700 group-hover:text-rose-700 transition-colors tracking-tight">
                                        Cerrar Sesión
                                    </p>
                                    <p className="text-[10px] text-slate-400 group-hover:text-rose-500 transition-colors font-medium">
                                        Salir de forma segura
                                    </p>
                                </div>
                            </div>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-rose-400 group-hover:translate-x-0.5 transition-all duration-300" />
                        </motion.button>
                    </div>

                    <div className="mt-4 text-center space-y-1">
                        <p className="text-[10px] font-semibold text-slate-400 tracking-wide">
                            © {new Date().getFullYear()} {userData?.nombre_empresa || 'Visioner7'}
                        </p>
                        <div className="flex items-center justify-center gap-1.5">
                            <div className={`w-1.5 h-1.5 rounded-full shadow-sm ${isAdmin ? 'bg-rose-500 shadow-rose-500/50' : 'bg-indigo-500 shadow-indigo-500/50'}`}></div>
                            <p className="text-[9px] text-slate-400 font-bold tracking-widest uppercase">
                                {isAdmin ? 'Admin Panel' : 'Visioner7'}
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Scrollbar */}
            <style>{`
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(148, 163, 184, 0.15) transparent;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(148, 163, 184, 0.15);
                    border-radius: 9999px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(148, 163, 184, 0.3);
                }
            `}</style>
        </motion.aside>
    );
};

export default Sidebar;